import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useConfig } from '../lib/api'
import { DocsPage } from '../pages/Docs'
import { Spinner } from './ui/misc'

/** The public docs site when the deployment has one (DOCS_URL), else null: the guide built into the console. */
export function useDocsUrl(): string | null {
  return useConfig().data?.docsUrl ?? null
}

/** A link to the API guide: a new tab on the docs site, or the in-console page. */
export function DocsLink({ className, children, onClick }: { className?: string; children: ReactNode; onClick?: () => void }) {
  const url = useDocsUrl()
  if (url)
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
        {children}
      </a>
    )
  return (
    <Link to="/docs" className={className} onClick={onClick}>
      {children}
    </Link>
  )
}

// sections of the in-console guide → their pages on the docs site, so old /docs#… links land in the right place
const PAGES: Record<string, string> = {
  overview: 'overview',
  auth: 'authentication',
  decide: 'decide',
  questions: 'question-types',
  response: 'responses',
  systemone: 'typesafe',
  models: 'models',
  errors: 'errors',
  limits: 'limits',
  tips: 'writing-questions',
}

/** /docs: off to the docs site when there is one, else the guide built into the console. */
export function DocsRoute() {
  const { data: config, isLoading } = useConfig()
  if (isLoading) return <Spinner />
  const url = config?.docsUrl
  if (!url) return <DocsPage />
  const page = PAGES[window.location.hash.slice(1)]
  window.location.replace(page ? `${url.replace(/\/+$/, '')}/${page}` : url)
  return <Spinner />
}
